import styled from 'styled-components';
import indents from '../../constants/js/indents.js';
import colors from '../../constants/js/colors.js';
import text from '../../constants/js/text.js';
import { borderRadius } from '../../constants/js/border';
import fieldStyles from '../../constants/js/fields';
const StyledIcon = styled.span.withConfig({
  componentId: "Input__sc-bs59e8-0"
})(["color:", ";transition:all 0.15s;position:absolute;top:50%;transform:translate(-50%,-50%);display:flex;align-items:center;"], ({
  isError
}) => isError ? colors.majorRed : colors.minorDarkBlue);
const StyledLoading = styled.div.withConfig({
  componentId: "Input__sc-bs59e8-1"
})(["position:absolute;top:50%;right:0;transform:translate(-50%,-50%);display:flex;align-items:center;"]);
const StyledBottomText = styled.div.withConfig({
  componentId: "Input__sc-bs59e8-2"
})(["font-size:", ";line-height:", ";color:", ";margin-top:", ";", ""], fieldStyles.bottomTextHint.fontSize, fieldStyles.bottomTextHint.fontSize, fieldStyles.bottomTextHint.themes.light.default, fieldStyles.bottomTextHint.topIndent, ({
  status
}) => status === 'error' && `
			color: ${fieldStyles.bottomTextHint.themes.light.error};
	`);
const StyledInputForFloatingLabel = `
	height: ${fieldStyles.field.size.h56.height};
	padding-top: ${fieldStyles.field.size.h56.paddingTop};
	padding-bottom: ${fieldStyles.field.size.h56.paddingBottom};
	font-size: ${fieldStyles.field.size.h56.fontSize};
	line-height: ${fieldStyles.field.size.h56.lineHeight};
`;
const StyledShrinkLabel = `
	& ~ label {
		font-size: ${text.fontSize7};
		line-height: ${text.lineHeight7};
		top: ${indents.indentXS};
		transform: none;
	}
`;
const StyledContainer = styled.div.withConfig({
  componentId: "Input__sc-bs59e8-3"
})(["display:block;position:relative;box-sizing:border-box;", " ", " ", " ", " ", " ", ""], ({
  isDisabled
}) => isDisabled && `
		cursor: not-allowed;

		${StyledInput} {
			pointer-events: none;
			color: ${fieldStyles.field.themes.light.default.disabledColor};
			background: ${fieldStyles.field.themes.light.default.disabledBackgroundColor};
			border-color: ${fieldStyles.field.themes.light.default.disabledBorderColor};

			&::placeholder {
				color: ${fieldStyles.field.themes.light.placeholder.disabledColor};
			}
		}
	`, ({
  ellipsis
}) => `
		${StyledInput} {
			text-overflow: ${ellipsis ? 'ellipsis' : 'unset'};
		}
	`, ({
  isWithIcon
}) => isWithIcon && `
			&& ${StyledInput} {
				padding-right: 40px;
			}
	`, ({
  size,
  isFloatingLabel
}) => size === 'large' && `
		${StyledInput} {
			font-size: ${fieldStyles.field.size.h48.fontSize};
			line-height: ${fieldStyles.field.size.h48.lineHeight};
			padding: ${fieldStyles.field.size.h48.paddingTop} ${fieldStyles.field.size.h48.paddingLeft};

			${isFloatingLabel ? StyledInputForFloatingLabel : ''}
		}

		${StyledIcon} {
			right: 10px;
		}
	` || size === 'medium' && `
		${StyledInput} {
			font-size: ${fieldStyles.field.size.h40.fontSize};
			line-height: ${fieldStyles.field.size.h40.lineHeight};
			padding: ${fieldStyles.field.size.h40.paddingTop} ${fieldStyles.field.size.h40.paddingLeft};
		}

		${StyledIcon} {
			right: ${indents.indentXS};
		}
	`, ({
  isCenteredPlaceholder
}) => isCenteredPlaceholder && `
		${StyledInput} {
			text-align: center;
		}
	`, ({
  isFloatingLabel,
  isShrink,
  placeholderColor = 'majorGray'
}) => isFloatingLabel && `
		${StyledInput} {
			&::placeholder {
				transition: ${isShrink ? 'all' : 'color 0.2s'};
				color: ${isShrink ? colors[placeholderColor] : 'transparent'};
			}
			
			&:focus {
				&::placeholder {
					color: ${fieldStyles.field.themes.light.placeholder.color};
				}
			}
		}
	`);
const borderSideStyles = {
  right: `border-radius: ${borderRadius} 0 0 ${borderRadius}; border-right: none;`,
  left: `border-radius: 0 ${borderRadius} ${borderRadius} 0; border-left: none;`,
  top: `border-radius: 0 0 ${borderRadius} ${borderRadius}; border-top: none;`,
  bottom: `border-radius: ${borderRadius} ${borderRadius} 0 0; border-bottom: none;`
};
const StyledInput = styled.input.withConfig({
  componentId: "Input__sc-bs59e8-4"
})(["outline:none;box-sizing:border-box;transition:color 0.2s ease-out,border-color 0.2s ease-out;width:100%;color:", ";border:1px solid ", ";font-family:inherit;margin:0;-webkit-appearance:none;-webkit-backface-visibility:hidden;&::-webkit-outer-spin-button,&::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}&::placeholder{color:", ";}&::-ms-clear{display:none;}&:hover{border:1px solid ", ";", "}&:focus{border-color:", ";", "}", " ", " ", " ", " &:not(:placeholder-shown){", "}", ""], ({
  color
}) => color ? colors[color] : fieldStyles.field.themes.light.default.color, fieldStyles.field.themes.light.default.borderColor, ({
  placeholderColor
}) => placeholderColor ? colors[placeholderColor] : fieldStyles.field.themes.light.placeholder.color, fieldStyles.field.themes.light.hovered.borderColor, ({
  hideBorderSide
}) => hideBorderSide && `border-${hideBorderSide}: none;`, fieldStyles.field.themes.light.focus.borderColor, StyledShrinkLabel, ({
  type,
  color = 'majorBlack'
}) => type === 'button' && `
			cursor: pointer;
			text-align: left;
			color: ${colors[color]};
			background: ${colors.majorWhite};
	`, ({
  status
}) => status === 'error' && `
			&, &:hover, &:focus {
				border-color: ${fieldStyles.field.themes.light.error.borderColor};
			}
	`, ({
  status
}) => status === 'warning' && `
			&, &:hover, &:focus {
				border-color: ${fieldStyles.field.themes.light.warning.borderColor};
			}
	`, ({
  hideBorderSide
}) => hideBorderSide ? borderSideStyles[hideBorderSide] : `border-radius: ${borderRadius};`, ({
  type
}) => type !== 'button' && StyledShrinkLabel, ({
  autoCompleteNativeStyles,
  disabled
}) => !autoCompleteNativeStyles && `
			&:-webkit-autofill,
			&:-webkit-autofill:hover,
			&:-webkit-autofill:focus,
			&:-webkit-autofill:active {
				-webkit-box-shadow: 0 0 0 30px ${colors.majorWhite} inset !important;
				-webkit-text-fill-color: ${disabled ? fieldStyles.field.themes.light.default.disabledColor : 'unset'}
			}
	`);
export { StyledInput, StyledContainer, StyledIcon, StyledLoading, StyledBottomText };